import React, {useCallback, useEffect, useMemo} from "react";

import {useRates} from "../../RateContext";
import Swiper from "../../../../components/Swiper";
import MonthVertical from "../../../../components/Planner/MonthVertical";
import AccommodationUnitSelector from "../../../../components/Accommodations/AccommodationUnitSelector";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {CircleCssIcon, PeopleTag, PriceTag, StatusTag, TextTag} from "../plannerTag";
import {formatPriceCent} from "../index";
import {Divider, Menu, MenuItem} from "@mui/material";
import ArtificialIntelligenceIcon from "../ArtificialIntelligenceIcon";

import PeopleIcon from "@mui/icons-material/People";
import NightsStayIcon from '@mui/icons-material/NightsStay';
import DoorFrontIcon from '@mui/icons-material/DoorFront';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import Fab from "@mui/material/Fab";


const getContentViews = (rate) => {
    const views = [];
    if (!rate) {
        return views;
    }

    for (let people = rate.maxPeople; people >= rate.minPeople; people--) {
        views.push({
            id: `price-${people}`,
            type: 'price',
            people,
            el: <Stack spacing={1} direction="row">
                <PeopleTag peopleNumber={people} iconSize="small"/>
                <div>Prezzo</div>
            </Stack>})
    }
    views.push({
        id: 'minPeople',
        type: 'minPeople',
        el: <Stack spacing={1} direction="row"><PeopleIcon fontSize="small"/><div>Min. persone</div></Stack>})
    views.push({
        id: 'checkin',
        type: 'checkin',
        el: <Stack spacing={1} direction="row"><SyncAltIcon fontSize="small"/><div>Arrivi</div></Stack>})
    views.push({
        id: 'checkout',
        type: 'checkout',
        el: <Stack spacing={1} direction="row"><SyncAltIcon fontSize="small"/><div>Partenze</div></Stack>})
    views.push({
        id: 'minNights',
        type: 'minNights',
        el: <Stack spacing={1} direction="row"><NightsStayIcon fontSize="small"/><div>Min. notti</div></Stack>})
    views.push({
        id: 'status',
        type: 'status',
        el: <Stack spacing={1} direction="row"><DoorFrontIcon fontSize="small"/><div>Stato</div></Stack>})

    return views;
}

const MobilePlanner = ({handleClickOutside, baseRate, formConfigurator}) => {
    const {accommodations, accUnitSelector} = useRates();
    const {selectNext, selectPrev} = accUnitSelector;
    const [contentView, setContentView] = React.useState(null);


    const renderDayCell = useCallback((dateISO, data) => {
        const dayData = data?.[dateISO];
        if (!dayData) {
            return null;
        }

        // switch case on selectedTab
        switch (contentView?.type) {
            case "price":
                const priceCent = dayData.pricesCent?.[contentView.people];
                if(!priceCent) {
                    return null;
                }
                return <PriceTag>€ {formatPriceCent(priceCent, contentView.people)}</PriceTag>;
            case "minPeople":
                return <TextTag>{dayData.minPeople}</TextTag>;
            case "checkin":
                if(!dayData.checkin) {
                    return null;
                }
                return <StatusTag><CircleCssIcon enabled/></StatusTag>;
            case "checkout":
                if(!dayData.checkout) {
                    return null;
                }
                return <StatusTag><CircleCssIcon enabled/></StatusTag>;
            case "minNights":
                return <TextTag>{dayData.minNights}</TextTag>;
            case "status":
                if (dayData.enabled === null) {
                    return null;
                }
                return <StatusTag enabled={dayData.enabled}><CircleCssIcon enabled={dayData.enabled===true}/></StatusTag>
            default:
                return null;
        }
    }, [contentView])

    return <Box onClick={handleClickOutside} sx={{display: 'flex', flexDirection: 'column', flexGrow: 1}}>
        <Swiper onNext={selectNext}
                onPrev={selectPrev}
                hasPrev={accommodations.accommodationUnits.length > 1}
                hasNext={accommodations.accommodationUnits.length > 1}
                headerEl={<AccommodationUnitSelector accommodationUnits={accommodations.accommodationUnits}
                                                     value={accUnitSelector.selectedAccommodationUnit}
                                                     onChange={accUnitSelector.handleChange}
                                                     isLoading={accommodations.isLoading}
                                                     align="center"/>}
                subHeaderEl={<PlanerToolbar baseRate={baseRate}
                                            contentView={contentView}
                                            setContentView={setContentView}/>}>
            <MonthVertical renderDayCell={renderDayCell}/>
        </Swiper>
        <Fab variant="extended"
             color="primary"
             size='medium'
             sx={{position: "fixed", bottom: 0, right: 0, mr: {xs: 2, md: 4}, mb: {xs: 2, md: 4}}}
             onClick={formConfigurator.open}>
            <ArtificialIntelligenceIcon simplified sx={{mr: 1}}/>
            Configura
        </Fab>
    </Box>
}

const PlanerToolbar = ({baseRate, contentView, setContentView}) => {

    return <>
        <Divider/>
        <Stack direction="row" p={1} justifyContent="space-evenly">
            <Typography fontWeight={500}>{baseRate?.name}</Typography>
            <ContentViewMenu rate={baseRate} contentView={contentView} setContentView={setContentView}/>
        </Stack>
    </>
}

const ContentViewMenu = ({rate, contentView, setContentView}) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSelect = (content) => {
        setContentView(content);
        handleClose();
    }

    const items = useMemo(() => getContentViews(rate), [rate]);

    // Set the base people as default content view
    useEffect(() => {
        if(rate?.basePeople) {
            setContentView(items.find(item => item.people === rate.basePeople))
        }
    }, [rate, items, setContentView])

    if (!rate) {
        return null;
    }

    return <>
        <Box onClick={handleClick}>
            {contentView?.el}
        </Box>
        <Menu anchorEl={anchorEl}
              open={open}
              onClose={handleClose}>
            {
                items.map(item => <MenuItem key={item.id}
                                            onClick={() => handleSelect(item)}>
                    {item.el}
                </MenuItem>)
            }
        </Menu>
    </>
}

export default MobilePlanner;